/**
 * AK-423 — вкладка «Геометрия» карточки поля v3 (эталон founder 2026-07-11)
 * + PR-E (подложка карты + «Изменить контур», фидбэк founder на staging).
 *
 * Слева — MapLibre мини-карта с подложкой (спутник Esri / схема OSM) и
 * эталонным контуром + зонами локальными GeoJSON-слоями (никакого MVT —
 * FieldContourMiniMap). Прежняя голая SVG-панель (контур на белом) убрана:
 * founder на staging увидел «серый полигон без карты». Справа — «Контуры поля»,
 * «Зоны поля» (площади, цветные маркеры, скрытие глазом), «Изменить контур»,
 * «Разделить поле на зоны…» и «История геометрий». Поверх существующего API
 * (/contours + /subzones + PUT /geometry) — модель/бэкенд не трогаем.
 *
 * AK-515: у зоны появился вход «Изменить границы» — тем же редактором, что и
 * контур поля, но сохранение уходит в PATCH /subzones/{id} с геометрией
 * (бэкенд был готов, не хватало входа в UI).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Circle,
  Eye,
  EyeOff,
  History,
  Loader2,
  Pencil,
  Scissors,
  Sprout,
  Trash2,
  Undo2,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { ApiError } from '@/lib/api';
import { toast } from '@/lib/toast';
import {
  FieldContourMiniMap,
  type MiniZone,
} from '@/components/map/FieldContourMiniMap';
import { useFieldContourGeo, type ContourGeo } from './widgets/widgetApi';
import {
  useContourHistory,
  useSubzones,
  useCreateSubzone,
  useDeleteSubzone,
  useRestoreFieldGeometry,
  useUpdateFieldGeometry,
  useUpdateSubzone,
} from './api';
import {
  buildPivotSplit,
  contourAreaHa,
  pluralZones,
  zoneVisual,
} from './geometryLogic';
import {
  buildZonePlan,
  defaultCircleName,
  type EditorShapes,
} from './zoneSplit';
import type { ContourVersion, Subzone } from './types';

const REFERENCE_ID = 'reference';
// Приоритет контура: эталон → рабочий → legacy-фолбэк → любой (как в FieldMiniMap).
// AK-517 / ADR-061: 'planting' убран (мёртвый тип); 'legacy' — синтетический
// ряд фолбэка AK-176 из fields.geometry.
const CONTOUR_PRIORITY = ['reference', 'working', 'legacy'];

function pickReferenceContour(contours?: ContourGeo[] | null): ContourGeo | null {
  if (!contours?.length) return null;
  const withGeo = contours.filter((c) => c.geojson?.coordinates);
  if (!withGeo.length) return null;
  for (const type of CONTOUR_PRIORITY) {
    const hit = withGeo.find((c) => c.contour_type === type);
    if (hit) return hit;
  }
  return withGeo[0];
}

/**
 * Разбор timestamptz из API. Бэкенд отдаёт `::text`, а это формат PostgreSQL
 * («2026-07-28 09:15:00+00»): пробел вместо `T` и смещение без двоеточия.
 * Chrome такое проглатывает, Safari — нет. Пробуем как есть, затем нормализуем.
 */
function parseApiTs(raw: string): Date {
  const direct = new Date(raw);
  if (!Number.isNaN(direct.getTime())) return direct;
  return new Date(raw.replace(' ', 'T'));
}

/** «Действует с 27.07.2026» для активной версии контура (AK-447). null — нет даты. */
function fmtValidFrom(raw?: string | null): string | null {
  if (!raw || raw === 'legacy') return null;
  const d = parseApiTs(raw);
  // Разобрать не вышло — показываем сырую строку, а не прочерк: дата, которую
  // пользователь сам вводил, не должна пропадать из-за формата (так же
  // фолбэчит fmtWhen в журнале).
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleDateString('ru-RU');
}

/** Дата + время для промежуточных сохранений («14.05.2026, 16:20»). */
function fmtMoment(raw?: string | null): string | null {
  if (!raw) return null;
  const d = parseApiTs(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleString('ru-RU', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function fmtHa(v: number | null | undefined): string {
  return v != null
    ? `${v.toLocaleString('ru-RU', { maximumFractionDigits: 1 })} га`
    : '—';
}

/** Сегодня в формате YYYY-MM-DD (локальная дата, не UTC). */
function todayISO(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** timestamptz из API → YYYY-MM-DD для `<input type="date">`. */
function dateInputValue(raw?: string | null): string {
  if (!raw) return '';
  const d = parseApiTs(raw);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/**
 * AK-515: внешнее кольцо границы зоны для редактора.
 *
 * Редактор правит ОДНУ фигуру, поэтому зону из нескольких кусков (склеенные
 * круги — MultiPolygon с несколькими частями) через него не отдаём: сохранение
 * молча потеряло бы остальные части. Такой зоне вход в правку границ закрыт
 * (кнопка задизейблена с объяснением), а не «работает наполовину».
 */
/** Почему у зоны из нескольких кусков нет правки границ (текст видим в строке). */
const ZONE_MULTIPART_NOTE =
  'Зона состоит из нескольких кусков — правка границ пока недоступна.';

function zoneEditRing(geo: Subzone['geojson']): number[][] | null {
  const coords = geo?.coordinates;
  if (!Array.isArray(coords)) return null;
  let ring: unknown = null;
  if (geo?.type === 'Polygon') ring = coords[0];
  else if (geo?.type === 'MultiPolygon' && coords.length === 1) {
    const first = coords[0];
    ring = Array.isArray(first) ? first[0] : null;
  }
  // Замкнутое кольцо — минимум 4 точки (последняя = первая).
  return Array.isArray(ring) && ring.length >= 4 ? (ring as number[][]) : null;
}

// ─── Строка слоя (контур / зона) ────────────────────────────────────────

function LayerRow({
  swatchClass,
  name,
  meta,
  hidden,
  onToggle,
  onEdit,
  editBlockedHint,
  editBlockedNote,
  editTestId,
  onDelete,
  footer,
}: {
  swatchClass: string;
  name: string;
  meta?: string;
  hidden: boolean;
  onToggle: () => void;
  /** AK-515: вход в правку границ зоны (у контура поля своя кнопка ниже). */
  onEdit?: () => void;
  /** Почему правка границ сейчас недоступна (кнопка остаётся, но выключена). */
  editBlockedHint?: string | null;
  /**
   * Постоянная причина запрета (свойство самой зоны, а не «сейчас занято»).
   * Печатается видимым текстом в строке: `title` на выключенной кнопке
   * недоступен с тача (hover-only взаимодействий у нас нет — CLAUDE.md).
   */
  editBlockedNote?: string | null;
  editTestId?: string;
  onDelete?: () => void;
  /** Доп. строка под названием (у зоны — «Зона существует с»). */
  footer?: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-muted/60">
      <span className={cn('h-3 w-3 shrink-0 rounded-sm', swatchClass)} aria-hidden />
      <div className="min-w-0 flex-1">
        <div className={cn('truncate text-sm', hidden && 'text-muted-foreground')}>
          {name}
        </div>
        {meta && <div className="text-xs text-muted-foreground">{meta}</div>}
        {editBlockedNote && (
          <div
            data-testid={editTestId ? `${editTestId}-note` : undefined}
            className="text-xs text-muted-foreground"
          >
            {editBlockedNote}
          </div>
        )}
        {footer}
      </div>
      {onEdit && (
        <button
          type="button"
          onClick={onEdit}
          disabled={Boolean(editBlockedHint)}
          data-testid={editTestId}
          aria-label={
            editBlockedHint
              ? `Изменить границы зоны «${name}» — недоступно: ${editBlockedHint}`
              : `Изменить границы зоны «${name}»`
          }
          title={editBlockedHint ?? 'Изменить границы зоны'}
          className="rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground disabled:pointer-events-none disabled:opacity-50"
        >
          <Pencil className="h-4 w-4" />
        </button>
      )}
      {onDelete && (
        <button
          type="button"
          onClick={onDelete}
          aria-label={`Удалить зону «${name}»`}
          title="Удалить зону"
          className="rounded-md p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      )}
      <button
        type="button"
        onClick={onToggle}
        aria-label={hidden ? `Показать ${name}` : `Скрыть ${name}`}
        aria-pressed={!hidden}
        title={hidden ? 'Показать' : 'Скрыть'}
        className="rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
      >
        {hidden ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
}

/**
 * Нижняя граница «Действует с». Всё, что раньше, — заведомо опечатка набора
 * (Chrome отдаёт промежуточные годы вида 0002 посегментно), а не реальная дата
 * появления поливного круга.
 */
const MIN_VALID_FROM = '1900-01-01';

/**
 * «Действует с» существующей зоны — с возможностью исправить дату.
 *
 * Зачем правка: дату зоны часто оставляют дефолтной (сегодня), а потом
 * выясняется, что круги полили ещё в мае. Пересоздавать зону нельзя — у неё
 * новый id, и заведённые в неё посевы остались бы на удалённой. Поэтому дату
 * рождения зоны правим на месте; границу это не трогает.
 *
 * ⚠️ Сохраняем по `blur`/Enter, а НЕ по `change` (ревью head-web). У
 * `<input type="date">` Chrome шлёт `change` на каждый «полный» промежуточный
 * вариант: пока набираешь год `2026`, контрол последовательно отдаёт
 * `0002-…`, `0020-…`, `0202-…`. Автосейв по `change` уносил на сервер год 0002
 * — а серверная валидация ловит только будущее, значит мусор сохранялся молча.
 * И это не косметика: `valid_from` зоны — T0 её ряда наблюдений, который потом
 * читает пакет 2.
 *
 * Поле контролируемое: пока правка не подтверждена, живёт локальный черновик,
 * а после отказа сервера (422) значение возвращается к серверному — панель не
 * должна показывать то, чего в базе нет. Во время сохранения инпут НЕ
 * дизейблим (иначе фокус рвётся посреди набора) — только спиннер + aria-busy.
 */
function ZoneValidFrom({
  zone,
  onFix,
}: {
  zone: Subzone;
  onFix: (zone: Subzone, date: string) => Promise<boolean>;
}) {
  const serverValue = dateInputValue(zone.valid_from);
  const [draft, setDraft] = useState(serverValue);
  const [saving, setSaving] = useState(false);
  // Сервер прислал другое значение (успешный PATCH → инвалидация → рефетч)
  // — подхватываем его как новую точку отсчёта.
  useEffect(() => setDraft(serverValue), [serverValue]);

  const inputId = `zone-valid-from-${zone.id}`;

  async function commit() {
    const next = draft;
    if (!next || next === serverValue) {
      setDraft(serverValue); // пустое/то же самое — просто откатываем черновик
      return;
    }
    if (next < MIN_VALID_FROM || next > todayISO()) {
      // Заведомо мусорную дату на сервер не гоним.
      setDraft(serverValue);
      toast.error('Проверьте дату: она должна быть в прошлом и не раньше 1900 года.');
      return;
    }
    // Конкурентный PATCH гасим здесь, в момент отправки, а не блокировкой поля
    // под курсором. Флаг локальный (на эту зону): глобальный `isPending`
    // молча съедал бы правку даты у соседней зоны.
    if (saving) return;
    setSaving(true);
    try {
      const ok = await onFix(zone, next);
      if (!ok) setDraft(serverValue); // сервер отказал — показываем то, что в базе
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mt-1 flex items-center gap-1">
      <Label htmlFor={inputId} className="text-xs text-muted-foreground">
        Зона существует с
      </Label>
      <Input
        id={inputId}
        data-testid={`zone-valid-from-${zone.id}`}
        type="date"
        className="h-6 w-32 px-1 text-xs"
        min={MIN_VALID_FROM}
        max={todayISO()}
        value={draft}
        aria-busy={saving}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => void commit()}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            void commit();
          }
        }}
      />
      {saving && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
    </div>
  );
}

// ─── Вкладка ────────────────────────────────────────────────────────────

interface Props {
  fieldId: string;
  /** farm_id поля — нужен NDVI-подложке мини-карты (AK-442, scope-фильтр тайлов). */
  farmId?: string | null;
  canWrite: boolean;
  /** Переход на другую вкладку карточки (см. detail.tsx gotoTab). */
  onGoto: (tab: string) => void;
  /** Геометрия поля изменилась — перезагрузить шапку карточки (площадь и т.п.). */
  onGeometryChanged?: () => void;
}

export function FieldGeometryTab({
  fieldId,
  farmId,
  canWrite,
  onGoto,
  onGeometryChanged,
}: Props) {
  const contours = useFieldContourGeo(fieldId);
  const subzones = useSubzones(fieldId);
  const createSubzone = useCreateSubzone(fieldId);
  const deleteZone = useDeleteSubzone(fieldId);
  const updateZone = useUpdateSubzone(fieldId);
  const updateGeometry = useUpdateFieldGeometry(fieldId);
  const history = useContourHistory(fieldId);
  const restoreGeometry = useRestoreFieldGeometry(fieldId);

  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState(false);
  // ── AK-460: инлайн-режим «Добавить поливные площади» ─────────────────────
  const [zonesMode, setZonesMode] = useState(false);
  const [shapes, setShapes] = useState<EditorShapes>([]);
  /** Имена фигур по индексу — агроном называет площадь при добавлении. */
  const [zoneNames, setZoneNames] = useState<string[]>([]);
  /** Сколько фигур уже «вырезано из основного поля» (шаг 4 сценария founder). */
  const [cutCount, setCutCount] = useState(0);
  /** AC-4: собрать круги в одну зону-мультиполигон (опция, не дефолт). */
  const [mergePivots, setMergePivots] = useState(false);
  const [zonesSeed, setZonesSeed] = useState<{ ring: number[][]; signal: number } | null>(null);
  const [zonesReset, setZonesReset] = useState(0);
  const [saving, setSaving] = useState(false);
  const seedSignal = useRef(0);
  // AK-447 «действует с»: по умолчанию сегодня — обычный случай «поправили
  // границу, она такая с сегодня».
  const [validFrom, setValidFrom] = useState(todayISO);
  // «Действует с» для НОВЫХ зон. Отдельное состояние: кейс founder «сейчас
  // июль, а круги надо завести маем» — дата зон и дата контура независимы.
  const [zonesValidFrom, setZonesValidFrom] = useState(todayISO);
  /** Версия контура, на которую собираемся откатиться (подтверждение). */
  const [rollbackTarget, setRollbackTarget] = useState<ContourVersion | null>(
    null,
  );
  // ── AK-515: правка границ существующей зоны ──────────────────────────────
  const [editZoneId, setEditZoneId] = useState<string | null>(null);
  /**
   * «Новая граница действует с». ⚠️ Это НЕ дата рождения зоны (её правит
   * ZoneValidFrom в строке списка): с геометрией бэкенд трактует `valid_from`
   * как дату, с которой действует НОВАЯ граница, и заводит версию в ленте, а
   * саму зону не «омолаживает» (докстринг field_card.update_subzone).
   */
  const [zoneGeomValidFrom, setZoneGeomValidFrom] = useState(todayISO);
  /** Свободная пометка «почему поправили» — уходит в ленту версий и журнал. */
  const [zoneGeomReason, setZoneGeomReason] = useState('');

  const referenceContour = useMemo(
    () => pickReferenceContour(contours.data),
    [contours.data],
  );
  const versions = useMemo<ContourVersion[]>(
    () => history.data ?? [],
    [history.data],
  );
  const referenceGeo = referenceContour?.geojson ?? null;
  const refAreaHa = useMemo(() => contourAreaHa(referenceGeo), [referenceGeo]);
  const zones = subzones.data ?? [];
  const hasZones = zones.length > 0;

  const miniZones = useMemo<MiniZone[]>(
    () =>
      zones.map((z, i) => ({
        id: z.id,
        geojson: z.geojson,
        kind: z.subzone_kind,
        index: i,
      })),
    [zones],
  );

  const editZone = useMemo(
    () => zones.find((z) => z.id === editZoneId) ?? null,
    [zones, editZoneId],
  );
  /** Что отдаём редактору: id + имя + нынешнее внешнее кольцо зоны. */
  const zoneEdit = useMemo(() => {
    if (!editZone) return null;
    const ring = zoneEditRing(editZone.geojson);
    return ring
      ? { id: editZone.id, name: editZone.name ?? 'Зона', ring }
      : null;
  }, [editZone]);

  function toggle(id: string) {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  /**
   * Исправить дату, с которой действует зона (границу не трогаем).
   * Возвращает `false`, если сервер отказал — поле откатится к тому, что в базе.
   */
  async function fixZoneDate(z: Subzone, date: string): Promise<boolean> {
    try {
      await updateZone.mutateAsync({
        subzoneId: z.id,
        body: { valid_from: date },
      });
      toast.success(`Зона «${z.name ?? 'без названия'}» действует с ${fmtValidFrom(date)}`);
      return true;
    } catch (err) {
      if (err instanceof ApiError) toast.error(err.detail);
      else toast.error('Не удалось изменить дату зоны');
      return false;
    }
  }

  function startZoneGeometryEdit(z: Subzone) {
    setEditZoneId(z.id);
    // Обычный случай — «граница такая с сегодня»; прошлое агроном выставит сам.
    setZoneGeomValidFrom(todayISO());
    setZoneGeomReason('');
  }

  /**
   * Сохранить НОВУЮ ГРАНИЦУ зоны (AK-515). Дата рождения зоны при этом не
   * трогается — бэкенд по наличию `geojson` понимает, что `valid_from` здесь
   * про границу. Ошибку не глушим: её показывает панель редактора на карте.
   */
  async function handleSaveZoneGeometry(geometry: {
    type: 'Polygon' | 'MultiPolygon';
    coordinates: unknown;
  }) {
    const z = editZone;
    if (!z) return;
    await updateZone.mutateAsync({
      subzoneId: z.id,
      body: {
        geojson: geometry,
        valid_from: zoneGeomValidFrom || undefined,
        change_reason: zoneGeomReason.trim() || undefined,
      },
    });
    toast.success(
      `Границы зоны «${z.name ?? 'без названия'}» сохранены — действуют с ${
        fmtValidFrom(zoneGeomValidFrom) ?? '—'
      }`,
    );
    onGeometryChanged?.();
  }

  async function handleDelete(z: Subzone) {
    try {
      await deleteZone.mutateAsync(z.id);
      toast.success(`Зона «${z.name ?? 'без названия'}» удалена`);
    } catch (err) {
      if (err instanceof ApiError) toast.error(err.detail);
      else toast.error('Не удалось удалить зону');
    }
  }

  /**
   * AK-447: сохраняем контур вместе с датой, с которой он действует. Дата
   * приходит из поля «Действует с» в правой панели (по умолчанию — сегодня);
   * второй аргумент — задел под перенос поля внутрь попапа карты (его вёрстку
   * ведёт head-web), сигнатура уже готова его принять.
   */
  async function handleSaveGeometry(
    geometry: { type: 'Polygon' | 'MultiPolygon'; coordinates: unknown },
    validFromArg?: string,
  ) {
    await updateGeometry.mutateAsync({
      geometry,
      valid_from: validFromArg ?? validFrom,
    });
    toast.success('Контур обновлён');
    onGeometryChanged?.();
  }

  // ── AK-460: план зон из нарисованных фигур ───────────────────────────────
  // Показываем площади ВСЕХ фигур (включая ещё не вырезанные), а сохраняем и
  // считаем богару — только по вырезанным.
  const cutShapes = useMemo(() => shapes.slice(0, cutCount), [shapes, cutCount]);
  // AK-494 сценарий 2: «вырезать круг → сохранить → вырезать второй → сохра-
  // нить» — вторая сессия редактора не помнит фигуры первой и всегда режет
  // от контура поля целиком. Передаём уже сохранённые активные зоны, чтобы
  // buildZonePlan мог (а) сразу и понятно отказать, если новая фигура физи-
  // чески легла внутрь ещё не удалённой старой зоны (типовой случай: круг +
  // «Богара» уже покрывают всё поле) — вместо непонятного 409 от бэкенд-
  // триггера постфактум; (б) если агроном старую «Богару» уже удалил —
  // корректно исключить оставшиеся зоны (напр. первый круг) из «остатка»
  // новой сессии.
  const existingZonesGeo = useMemo(() => zones.map((z) => z.geojson), [zones]);
  const planAll = useMemo(
    () =>
      buildZonePlan(referenceGeo, shapes, {
        names: zoneNames,
        mergePivots,
        existingZones: existingZonesGeo,
      }),
    [referenceGeo, shapes, zoneNames, mergePivots, existingZonesGeo],
  );
  const planCut = useMemo(
    () =>
      buildZonePlan(referenceGeo, cutShapes, {
        names: zoneNames,
        mergePivots,
        existingZones: existingZonesGeo,
      }),
    [referenceGeo, cutShapes, zoneNames, mergePivots, existingZonesGeo],
  );
  const pendingCount = Math.max(0, shapes.length - cutCount);
  const restZone = planCut.ok ? planCut.zones.find((z) => z.isRest) ?? null : null;

  /** Площадь фигуры (по её индексу) для строки списка. */
  const areaOfShape = useCallback(
    (i: number): number | null => {
      if (!planAll.ok) return null;
      return planAll.zones.find((z) => z.shapeIndex === i)?.areaHa ?? null;
    },
    [planAll],
  );

  /**
   * Что показать в поле имени: заданное агрономом, иначе — РОВНО то имя,
   * которое подставит план (круг/зона, с номером или без, в зависимости от
   * количества кругов). Так подпись в списке и имя сохранённой зоны совпадают.
   */
  const nameOf = useCallback(
    (i: number): string =>
      zoneNames[i] ??
      (planAll.ok ? planAll.zones.find((z) => z.shapeIndex === i)?.name : undefined) ??
      defaultCircleName(i + 1, shapes.length),
    [zoneNames, planAll, shapes.length],
  );

  function setNameAt(i: number, value: string) {
    setZoneNames((prev) => {
      const next = [...prev];
      // Дырки заполняем текущими подписями, чтобы правка i-й строки не сбила
      // имена предыдущих.
      while (next.length <= i) next.push(nameOf(next.length));
      next[i] = value;
      return next;
    });
  }

  function enterZonesMode() {
    setZonesMode(true);
    setShapes([]);
    setZoneNames([]);
    setCutCount(0);
    setZonesSeed(null);
    setZonesValidFrom(todayISO());
  }

  /** Откат контура на выбранную сохранённую геометрию. */
  async function handleRollback() {
    const target = rollbackTarget;
    if (!target) return;
    try {
      await restoreGeometry.mutateAsync({
        history_id: target.id,
        source: target.source,
      });
      toast.success(
        `Контур восстановлен из версии от ${
          fmtValidFrom(target.valid_from) ?? '—'
        }`,
      );
      setRollbackTarget(null);
      onGeometryChanged?.();
    } catch (err) {
      if (err instanceof ApiError) toast.error(err.detail);
      else toast.error('Не удалось восстановить контур');
    }
  }

  function exitZonesMode() {
    setZonesMode(false);
    setShapes([]);
    setZoneNames([]);
    setCutCount(0);
    setZonesSeed(null);
  }

  function handleShapesChange(next: EditorShapes) {
    setShapes(next);
    // Фигур стало меньше (undo/очистка) — вырезанных не может быть больше.
    setCutCount((c) => Math.min(c, next.length));
    // Имена НЕ преднаполняем: массив держим разреженным, чтобы подпись
    // оставалась «живым» дефолтом плана («Орошаемый круг» → «Орошаемый круг 1»
    // когда появился второй) и фиксировалась только там, где агроном её ввёл.
  }

  function applyPivotPreset() {
    const split = buildPivotSplit(referenceGeo);
    const ring = split?.circle.coordinates?.[0];
    if (!ring?.length) {
      toast.error('Не удалось вписать круг в контур поля — нарисуйте вручную.');
      return;
    }
    seedSignal.current += 1;
    setCutCount(0);
    setZonesSeed({ ring: ring as number[][], signal: seedSignal.current });
  }

  /** Шаг 8 сценария: одним действием создаём все зоны (атомарно, #493). */
  async function saveZones() {
    if (!planCut.ok || saving) return;
    setSaving(true);
    const created: string[] = [];
    try {
      for (const z of planCut.zones) {
        const row = await createSubzone.mutateAsync({
          subzone_kind: z.kind,
          overlap_mode: 'overlay',
          name: z.name,
          geojson: z.geojson,
          valid_from: zonesValidFrom || undefined,
        });
        created.push(row.id);
      }
      toast.success(`Сохранено: ${pluralZones(planCut.zones.length)}`);
      exitZonesMode();
    } catch (err) {
      const detail = err instanceof ApiError ? err.detail : 'неизвестная ошибка';
      if (!created.length) {
        toast.error(`Не удалось сохранить зоны: ${detail}.`);
        return;
      }
      let rolledBack = true;
      for (const id of [...created].reverse()) {
        try {
          await deleteZone.mutateAsync(id);
        } catch {
          rolledBack = false;
        }
      }
      toast.error(
        rolledBack
          ? `Не удалось сохранить зоны (${detail}). Изменения отменены.`
          : `Не удалось сохранить зоны (${detail}), и откат не сработал — удалите созданные вручную.`,
      );
    } finally {
      setSaving(false);
    }
  }

  const loading = contours.isLoading || subzones.isLoading;

  return (
    <div className="py-3">
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1fr_20rem]">
        {/* Карта-панель */}
        <div className="relative aspect-[4/3] max-h-[75vh] min-h-[24rem] w-full overflow-hidden rounded-xl border border-border bg-muted/30">
          {loading ? (
            <Skeleton className="absolute inset-0 h-full w-full" />
          ) : (
            <FieldContourMiniMap
              fieldId={fieldId}
              farmId={farmId}
              referenceGeo={referenceGeo}
              zones={miniZones}
              hidden={hidden}
              referenceHidden={hidden.has(REFERENCE_ID)}
              editing={editing}
              onEditingChange={setEditing}
              onSaveGeometry={handleSaveGeometry}
              zonesEditing={zonesMode}
              onZoneShapesChange={handleShapesChange}
              zonesSeed={zonesSeed}
              zonesResetSignal={zonesReset}
              zoneEdit={zoneEdit}
              onSaveZoneGeometry={handleSaveZoneGeometry}
              onZoneEditCancel={() => setEditZoneId(null)}
            />
          )}
          {!editing && !zonesMode && !zoneEdit && (
            <div className="pointer-events-none absolute left-2 top-2 rounded-md bg-card/85 px-2 py-1 text-xs text-muted-foreground shadow-sm">
              Эталонный контур{refAreaHa != null ? ` · ${fmtHa(refAreaHa)}` : ''}
            </div>
          )}
        </div>

        {/* Правая панель */}
        <div className="space-y-4 rounded-xl border border-border bg-card p-3">
          {/* AK-460: режим «Добавить поливные площади» — вместо модалки. */}
          {zonesMode && (
            <div className="space-y-3" data-testid="zones-panel">
              <div>
                <h4 className="text-sm font-semibold text-foreground">
                  Поливные площади
                </h4>
                <p className="mt-1 text-xs text-muted-foreground">
                  Обведите круг полива на снимке, назовите его и вырежьте из
                  поля. Кругов может быть несколько.
                </p>
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 text-xs"
                  disabled={saving}
                  onClick={applyPivotPreset}
                >
                  Вписать круг
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 text-xs"
                  disabled={saving}
                  onClick={() => {
                    setCutCount(0);
                    setZoneNames([]);
                    setZonesSeed(null);
                    setZonesReset((n) => n + 1);
                  }}
                >
                  Начать заново
                </Button>
              </div>

              {/* Список фигур: имя задаёт агроном (шаг 4 сценария). */}
              {shapes.length === 0 ? (
                <p className="rounded-lg bg-muted px-3 py-2 text-xs text-muted-foreground">
                  Инструмент «Круг» уже выбран — тяните по снимку от центра к
                  краю.
                </p>
              ) : (
                <ul className="space-y-2" data-testid="zones-draft-list">
                  {shapes.map((_, i) => (
                    <li key={i} className="space-y-1 rounded-lg border border-border p-2">
                      <div className="flex items-center gap-2">
                        <span
                          className={cn(
                            'h-3 w-3 shrink-0 rounded-sm',
                            zoneVisual('irrigated_pivot', i).bg,
                          )}
                          aria-hidden
                        />
                        <Input
                          aria-label={`Название площади ${i + 1}`}
                          className="h-7 flex-1 text-sm"
                          value={nameOf(i)}
                          onChange={(e) => setNameAt(i, e.target.value)}
                        />
                      </div>
                      <div className="flex items-center justify-between px-1 text-xs text-muted-foreground">
                        <span>{fmtHa(areaOfShape(i))}</span>
                        <span>
                          {i < cutCount ? 'вырезана из поля' : 'ещё не вырезана'}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              {pendingCount > 0 && (
                <Button
                  className="w-full"
                  data-testid="zones-cut"
                  disabled={saving || !planAll.ok}
                  onClick={() => setCutCount(shapes.length)}
                >
                  <Scissors className="mr-2 h-4 w-4" />
                  Вырезать из основного поля
                </Button>
              )}

              {/* Остаток — live-пересчёт (шаг 6 сценария). */}
              {restZone && (
                <div
                  className="flex items-center gap-2 rounded-lg bg-accent-brand/10 px-3 py-2 text-sm"
                  data-testid="zones-rest"
                >
                  <span
                    className={cn(
                      'h-3 w-3 shrink-0 rounded-sm',
                      zoneVisual('dryland_corner', 0).bg,
                    )}
                    aria-hidden
                  />
                  <span className="flex-1 truncate">{restZone.name}</span>
                  <span className="text-xs text-muted-foreground">
                    {fmtHa(restZone.areaHa)}
                  </span>
                </div>
              )}

              {shapes.length > 1 && (
                <label className="flex items-start gap-2 px-1 text-xs text-muted-foreground">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={mergePivots}
                    onChange={(e) => setMergePivots(e.target.checked)}
                  />
                  <span>Считать все круги одной зоной (мультиполигон)</span>
                </label>
              )}

              {/* С какой даты зоны существуют. Бывает нужно завести майские
                  круги в июле — прошлое разрешено, будущее сервер отклонит. */}
              <div className="space-y-1 rounded-lg border border-border px-3 py-2">
                <Label htmlFor="zones-valid-from" className="text-xs">
                  Действует с
                </Label>
                <Input
                  id="zones-valid-from"
                  data-testid="zones-valid-from"
                  type="date"
                  className="h-8"
                  max={todayISO()}
                  value={zonesValidFrom}
                  onChange={(e) => setZonesValidFrom(e.target.value)}
                />
                <p className="text-xs leading-snug text-muted-foreground">
                  С этой даты поле считается по зонам. Можно указать прошедшую
                  дату — например, если круги полили ещё в мае.
                </p>
              </div>

              {!planAll.ok && shapes.length > 0 && (
                <p className="text-xs text-destructive">{planAll.error}</p>
              )}

              <div className="flex gap-2">
                <Button
                  className="flex-1"
                  data-testid="zones-save"
                  disabled={saving || cutCount === 0 || !planCut.ok}
                  onClick={() => void saveZones()}
                >
                  {saving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                  Сохранить
                  {planCut.ok && cutCount > 0 ? ` (${planCut.zones.length})` : ''}
                </Button>
                <Button variant="outline" disabled={saving} onClick={exitZonesMode}>
                  <X className="mr-1 h-4 w-4" />
                  Отмена
                </Button>
              </div>
            </div>
          )}

          {!zonesMode && hasZones && (
            <div className="flex items-center gap-2 rounded-lg bg-primary/10 px-3 py-3 text-sm text-primary">
              <Sprout className="h-4 w-4 shrink-0" />
              <span className="flex-1">
                Поле разделено на {pluralZones(zones.length)}
              </span>
              <Button
                size="sm"
                className="h-7 px-3"
                onClick={() => onGoto('rotation')}
              >
                Задать посев →
              </Button>
            </div>
          )}

          {!zonesMode && (
          <>
          {/* Контуры поля */}
          <section className="space-y-1">
            <h4 className="px-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Контуры поля
            </h4>
            {loading ? (
              <Skeleton className="h-9 w-full" />
            ) : referenceGeo ? (
              <LayerRow
                swatchClass="bg-muted-foreground"
                name="Эталонный (всё поле)"
                meta={refAreaHa != null ? fmtHa(refAreaHa) : undefined}
                hidden={hidden.has(REFERENCE_ID)}
                onToggle={() => toggle(REFERENCE_ID)}
              />
            ) : (
              <p className="px-2 text-sm text-muted-foreground">
                Контур поля пока не задан.
              </p>
            )}
          </section>

          {/* Зоны поля */}
          <section className="space-y-1">
            <div className="flex items-center justify-between px-2">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Зоны поля
              </h4>
              <span className="text-xs text-muted-foreground">
                {hasZones ? pluralZones(zones.length) : 'нет'}
              </span>
            </div>
            {loading ? (
              <Skeleton className="h-9 w-full" />
            ) : hasZones ? (
              zones.map((z, i) => (
                <LayerRow
                  key={z.id}
                  swatchClass={zoneVisual(z.subzone_kind, i).bg}
                  name={z.name ?? 'Зона'}
                  meta={z.area_ha != null ? fmtHa(z.area_ha) : undefined}
                  hidden={hidden.has(z.id)}
                  onToggle={() => toggle(z.id)}
                  onEdit={
                    canWrite ? () => startZoneGeometryEdit(z) : undefined
                  }
                  editTestId={`zone-edit-geometry-${z.id}`}
                  editBlockedHint={
                    editing
                      ? 'Сначала закончите правку контура поля.'
                      : editZoneId && editZoneId !== z.id
                        ? 'Сначала закончите правку границ другой зоны.'
                        : zoneEditRing(z.geojson)
                          ? null
                          : ZONE_MULTIPART_NOTE
                  }
                  // Постоянный запрет печатаем в строке: на тач-устройствах
                  // `title` выключенной кнопки пользователю недоступен.
                  editBlockedNote={
                    canWrite && !zoneEditRing(z.geojson)
                      ? ZONE_MULTIPART_NOTE
                      : null
                  }
                  onDelete={canWrite ? () => handleDelete(z) : undefined}
                  footer={
                    canWrite ? (
                      <ZoneValidFrom zone={z} onFix={fixZoneDate} />
                    ) : (
                      fmtValidFrom(z.valid_from) && (
                        <div className="text-xs text-muted-foreground">
                          Зона существует с {fmtValidFrom(z.valid_from)}
                        </div>
                      )
                    )
                  }
                />
              ))
            ) : (
              <p className="px-2 text-sm text-muted-foreground">
                Поле пока не разделено — сеется целиком.
              </p>
            )}

            {/* Предупреждение о зонах при правке контура. */}
            {canWrite && hasZones && editing && (
              <p className="mt-2 rounded-lg bg-accent-brand/10 px-3 py-2 text-xs text-foreground">
                У поля есть зоны — проверьте их границы после изменения контура.
              </p>
            )}

            {/* AK-447: с какой даты действует новый контур. Ею же закрывается
                прежняя версия — история геометрий получает интервалы. */}
            {canWrite && editing && (
              <div className="mt-2 space-y-1 rounded-lg border border-border px-3 py-2">
                <Label htmlFor="contour-valid-from" className="text-xs">
                  Действует с
                </Label>
                <Input
                  id="contour-valid-from"
                  type="date"
                  className="h-8"
                  value={validFrom}
                  onChange={(e) => setValidFrom(e.target.value)}
                />
                <p className="text-xs leading-snug text-muted-foreground">
                  С этой даты поле считается по новому контуру. Прежний останется
                  в истории.
                </p>
              </div>
            )}

            {/* AK-515: параметры правки границ зоны. Сохранение — кнопкой на
                карте (там же площадь на лету), здесь — только смыслы даты и
                пометка «почему». */}
            {canWrite && zoneEdit && (
              <div
                data-testid="zone-geometry-panel"
                className="mt-2 space-y-1 rounded-lg border border-border px-3 py-2"
              >
                <p className="text-xs font-semibold text-foreground">
                  Границы зоны «{zoneEdit.name}»
                </p>
                <Label htmlFor="zone-geom-valid-from" className="text-xs">
                  Новая граница действует с
                </Label>
                <Input
                  id="zone-geom-valid-from"
                  data-testid="zone-geom-valid-from"
                  type="date"
                  className="h-8"
                  min={dateInputValue(editZone?.valid_from) || MIN_VALID_FROM}
                  max={todayISO()}
                  value={zoneGeomValidFrom}
                  onChange={(e) => setZoneGeomValidFrom(e.target.value)}
                />
                <p className="text-xs leading-snug text-muted-foreground">
                  Это дата НОВОЙ границы, а не дата появления зоны: «Действует
                  с» в строке зоны правка границ не меняет. Прежняя граница
                  останется в истории зоны.
                </p>
                <Label htmlFor="zone-geom-reason" className="text-xs">
                  Почему поправили (необязательно)
                </Label>
                <Input
                  id="zone-geom-reason"
                  data-testid="zone-geom-reason"
                  className="h-8"
                  placeholder="Например: уточнили круг по снимку"
                  value={zoneGeomReason}
                  onChange={(e) => setZoneGeomReason(e.target.value)}
                />
                <p className="text-xs leading-snug text-muted-foreground">
                  Двигайте вершины на карте слева, там же «Сохранить».
                </p>
              </div>
            )}

            {/* Изменить контур — НАД «Разделить на зоны» (простая правка границы). */}
            {canWrite && !loading && (
              <Button
                variant="outline"
                className="mt-2 w-full"
                disabled={!referenceGeo || editing || Boolean(editZoneId)}
                onClick={() => setEditing(true)}
              >
                <Pencil className="mr-2 h-4 w-4" />
                Изменить контур
              </Button>
            )}

            {canWrite && !loading && !hasZones && (
              <Button
                variant="outline"
                className="w-full"
                disabled={!referenceGeo || editing || Boolean(editZoneId)}
                data-testid="zones-enter"
                onClick={enterZonesMode}
              >
                <Circle className="mr-2 h-4 w-4" />
                Добавить поливные площади
              </Button>
            )}
            {canWrite && hasZones && (
              <p className="px-2 pt-1 text-xs text-muted-foreground">
                Чтобы разделить иначе — удалите зоны.
              </p>
            )}
          </section>

          {/* История геометрий — реальный список сохранённых версий + откат. */}
          <section className="space-y-1">
            <h4 className="px-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              История геометрий
            </h4>
            {loading || history.isLoading ? (
              <Skeleton className="h-9 w-full" />
            ) : history.isError ? (
              <p className="px-2 text-sm text-muted-foreground">
                Не удалось загрузить историю контура.
              </p>
            ) : versions.length > 0 ? (
              /* Пакет 1 пишет КАЖДОЕ сохранение геометрии — у активно
                 правленного поля это десятки строк. Панель узкая (20rem):
                 без ограничения высоты правая колонка распирается и вкладка
                 превращается в простыню (CLAUDE.md «Адаптивная вёрстка»). */
              <ul
                className="max-h-72 space-y-1 overflow-y-auto overscroll-contain"
                data-testid="contour-history"
              >
                {versions.map((v) => (
                  <li
                    key={`${v.source}:${v.id}`}
                    className="flex items-start gap-2 rounded-lg px-2 py-2 hover:bg-muted/60"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-sm">
                        <span className={cn(v.is_current && 'font-medium')}>
                          {v.is_current ? 'Действует сейчас' : v.what}
                        </span>
                        {v.area_ha != null && (
                          <span className="text-muted-foreground">
                            {' '}
                            · {fmtHa(v.area_ha)}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {v.source === 'edit'
                          ? `Сохранено ${fmtMoment(v.valid_from) ?? '—'}`
                          : `Действует с ${fmtValidFrom(v.valid_from) ?? '—'}`}
                        {v.who_name ? ` · ${v.who_name}` : ''}
                      </div>
                      {v.reason && (
                        <div className="text-xs text-muted-foreground">
                          {v.reason}
                        </div>
                      )}
                    </div>
                    {canWrite && !v.is_current && (
                      <button
                        type="button"
                        onClick={() => setRollbackTarget(v)}
                        aria-label="Восстановить эту геометрию"
                        title="Восстановить эту геометрию"
                        className="rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
                      >
                        <Undo2 className="h-4 w-4" />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            ) : referenceGeo ? (
              <div className="rounded-lg px-2 py-2 text-sm">
                <span className="font-medium">Действует сейчас</span>
                {refAreaHa != null && (
                  <span className="text-muted-foreground"> · {fmtHa(refAreaHa)}</span>
                )}
                {fmtValidFrom(referenceContour?.valid_from) && (
                  <div className="text-xs text-muted-foreground">
                    Действует с {fmtValidFrom(referenceContour?.valid_from)}
                  </div>
                )}
              </div>
            ) : (
              <p className="px-2 text-sm text-muted-foreground">Пока пусто.</p>
            )}
          </section>
          </>
          )}
        </div>
      </div>

      {deleteZone.isPending && (
        <div className="mt-2 flex items-center gap-2 px-1 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Обновляю зоны…
        </div>
      )}

      {/* Подтверждение отката: контур поля меняется у всех, кто его видит. */}
      <Dialog
        open={rollbackTarget !== null}
        onOpenChange={(o) => {
          if (!o) setRollbackTarget(null);
        }}
      >
        <DialogContent data-testid="contour-rollback-dialog">
          <DialogHeader>
            <DialogTitle>Восстановить прежний контур?</DialogTitle>
            <DialogDescription>
              Поле снова будет считаться по геометрии от{' '}
              {rollbackTarget?.source === 'edit'
                ? (fmtMoment(rollbackTarget?.valid_from) ?? '—')
                : (fmtValidFrom(rollbackTarget?.valid_from) ?? '—')}
              {rollbackTarget?.area_ha != null
                ? ` (${fmtHa(rollbackTarget.area_ha)})`
                : ''}
              . Нынешняя геометрия останется в истории — вернуться к ней можно
              будет тем же способом.
            </DialogDescription>
          </DialogHeader>
          {hasZones && (
            <p className="rounded-lg bg-accent-brand/10 px-3 py-2 text-xs text-foreground">
              У поля есть зоны — после восстановления проверьте их границы.
            </p>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setRollbackTarget(null)}
            >
              Отмена
            </Button>
            <Button
              type="button"
              data-testid="contour-rollback-confirm"
              disabled={restoreGeometry.isPending}
              onClick={() => void handleRollback()}
            >
              {restoreGeometry.isPending && (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              )}
              <History className="mr-1 h-4 w-4" />
              Восстановить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default FieldGeometryTab;
